import React, { useState, useEffect } from "react";
import api from "../../axios/api";
import "../../assets/NewFiles/Css/Dashboard.css";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { 
  Box, 
  TableContainer, 
  Table, 
  Paper, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody,
  Typography,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Divider,
  Grid
} from "@mui/material";
import { FaCheckCircle, FaFileExcel, FaUserFriends } from "react-icons/fa";
import { useParams } from "react-router-dom";
import * as XLSX from 'xlsx';
import { toast } from "react-toastify";
import Button from "../common/styled components/Button";
import CommonGrid from "../common/Grid";
import Loader from "../common/Loader";

const ProjectContributors = () => {
  const [users, setUsers] = useState([]);
  const [projectData, setProjectData] = useState({});
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const response = await api.get(`/api/project/getProjectContributors/${id}`);
        setProjectData(response.data.projectData);
        setUsers(response.data.users);
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.error("Failed to load project contributors");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [id]);

  const filteredUsers = users.filter(
    (user) => user.role_code !== "admin" && user.role_code !== "superadmin"
  );

  const exportToExcel = () => {
    try {
      const wb = XLSX.utils.book_new();

      // Create project details array first
      const projectDetails = [
        ['Project Details', ''],
        ['Name', projectData?.name || ''],
        ['Start Date', projectData?.start_date || ''],
        ['End Date', projectData?.end_date || ''],
        ['Description', projectData?.big_desc || ''],
        ['Total Trees Target', projectData?.total_trees_target || ''],
        ['Total Trees Planted', projectData?.total_trees_planted || ''],
        ['Tags', projectData?.tags],
        ['ESG Goals', projectData?.esg_goals],
        ['Address', projectData?.address || ''],
        [], // Empty row for spacing
      ];

      // Create contributors data array
      const contributorsData = [
        [
          'Full Name',
          'Email',
          'Verification Status',
          'Role',
          'Total Trees',
          'Tree Growth',
          'Points Collected',
        ],
        ...filteredUsers.map(user => [
          user.full_name,
          user.userEmail,
          user.isVerified ? 'Verified' : 'Unverified',
          user.role_code,
          user.tree_uploaded_count,
          user.tree_history_updated_count,
          user.total_claimed_points,
        ])
      ];

      // Convert arrays to worksheet
      const ws = XLSX.utils.aoa_to_sheet([...projectDetails, ...contributorsData]);

      // Set column widths
      const maxWidth = 20;
      ws['!cols'] = Array(8).fill({ wch: maxWidth });

      // Create style objects
      const headerStyle = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "4F81BD" } },
        alignment: { vertical: 'center', horizontal: 'left' }
      };

      const labelStyle = {
        font: { bold: true },
        fill: { fgColor: { rgb: "DCE6F1" } },
        alignment: { vertical: 'center', horizontal: 'left' }
      };

      const contributorsHeaderStyle = {
        font: { bold: true },
        fill: { fgColor: { rgb: "E6F3FF" } },
        alignment: { vertical: 'center', horizontal: 'left' }
      };

      // Apply styles using cell references
      const range = XLSX.utils.decode_range(ws['!ref']);

      // Style project details header (first row)
      ws[XLSX.utils.encode_cell({ r: 0, c: 0 })] = {
        ...ws[XLSX.utils.encode_cell({ r: 0, c: 0 })],
        s: headerStyle
      };
      ws[XLSX.utils.encode_cell({ r: 0, c: 1 })] = {
        ...ws[XLSX.utils.encode_cell({ r: 0, c: 1 })],
        s: headerStyle
      };

      // Style project details
      for (let i = 1; i < projectDetails.length - 1; i++) {
        const cellA = XLSX.utils.encode_cell({ r: i, c: 0 });
        const cellB = XLSX.utils.encode_cell({ r: i, c: 1 });

        if (ws[cellA]) ws[cellA].s = labelStyle;
        if (ws[cellB]) ws[cellB].s = labelStyle;
      }

      // Style contributors header
      const contributorsHeaderRow = projectDetails.length;
      for (let i = 0; i < 7; i++) {
        const cell = XLSX.utils.encode_cell({ r: contributorsHeaderRow, c: i });
        if (ws[cell]) ws[cell].s = contributorsHeaderStyle;
      }

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, "Project Details & Contributors");

      // Generate file name with current date
      const fileName = `project_report_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Save file
      XLSX.writeFile(wb, fileName);

      toast.success("Data exported successfully!");
    } catch (error) {
      console.error("Error exporting data:", error);
      toast.error("Failed to export data");
    }
  };

  // Mobile card view for user data
  const MobileUserCard = ({ user }) => (
    <Card sx={{ mb: 2, boxShadow: 1 }}>
      <CardContent>
        <Typography variant="h6" component="div" sx={{ mb: 1 }}>
          {user.full_name}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {user.isVerified ? (
            <FaCheckCircle color="green" style={{ marginRight: '8px' }} title="Verified User" />
          ) : (
            <FaCheckCircle color="red" style={{ marginRight: '8px' }} title="Unverified User" />
          )}
          <Typography variant="body2">{user.userEmail}</Typography>
        </Box>
        
        <Divider sx={{ my: 1 }} />
        
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Role:</Typography>
            <Typography variant="body1">{user.role_code}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Total Trees:</Typography>
            <Typography variant="body1">{user.tree_uploaded_count}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Tree Growth:</Typography>
            <Typography variant="body1">{user.tree_history_updated_count}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Points:</Typography>
            <Typography variant="body1">{user.total_claimed_points}</Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <div>
      <Box sx={{ width: "100%", margin: "auto", padding: isMobile ? 1 : 2 }}>
        <ToastContainer />

        {isLoading && (
          <div className="loading-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 1000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Loader />
          </div>
        )}
        
        <div className={isLoading ? 'content-disabled' : ''} style={{
          pointerEvents: isLoading ? 'none' : 'auto',
          opacity: isLoading ? 0.6 : 1,
          transition: 'opacity 0.3s'
        }}>
          <Box sx={{ padding: isMobile ? 1 : 2 }}>
            {/* Responsive header section */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'flex-start' : 'center', 
              gap: isMobile ? 2 : 0,
              marginBottom: 2 
            }}>
              {/* Back button */}
              <Button
                onClick={() => navigate(-1)}
                sx={{ 
                  fontSize: isMobile ? '0.8rem' : '0.9rem',
                  padding: isMobile ? '6px 12px' : '8px 16px',
                  width: isMobile ? 'fit-content' : 'auto'
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ marginRight: '4px' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Projects
              </Button>

              {!isMobile && <Box sx={{ flexGrow: 1 }} />}
              
              {/* Project title - full width on mobile */}
              <Box sx={{ 
                textAlign: isMobile ? 'left' : 'center', 
                flexGrow: isMobile ? 0 : 2,
                width: isMobile ? '100%' : 'auto',
                my: isMobile ? 1 : 0
              }}>
                <Typography 
                  variant={isMobile ? "h5" : "h4"} 
                  component="h2"
                  sx={{ 
                    fontWeight: 'bold',
                    wordBreak: 'break-word'
                  }}
                >
                  {projectData?.name || ''}
                </Typography>
              </Box>
              
              {!isMobile && <Box sx={{ flexGrow: 1 }} />}
              
              {/* Export button - full width on mobile */}
              <Button
                variant="contained"
                startIcon={<FaFileExcel />}
                onClick={exportToExcel}
                disabled={filteredUsers.length === 0}
                sx={{
                  backgroundColor: "#1D6F42",
                  '&:hover': {
                    backgroundColor: "#15552F",
                  },
                  width: isMobile ? '100%' : 'auto',
                  fontSize: isMobile ? '0.8rem' : '0.9rem',
                  padding: isMobile ? '8px 12px' : '8px 16px',
                }}
              >
                Export to Excel
              </Button>
            </Box>

            {/* No data state */}
            {!isLoading && filteredUsers.length === 0 ? (
              <Box 
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 8,
                  textAlign: 'center'
                }}
              >
                <Box sx={{ mb: 4, color: '#666', fontSize: '80px' }}>
                  <FaUserFriends style={{ margin: '0 auto' }} />
                </Box>
                <Typography variant="h5" component="h3" gutterBottom>
                  No Contributors Found
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500 }}>
                  This project doesn't have any contributors yet. Contributors will appear here once they are added to the project.
                </Typography>
              </Box>
            ) : isMobile ? (
              <Box sx={{ mt: 2 }}>
                {filteredUsers.map((user) => (
                  <MobileUserCard key={user.id} user={user} />
                ))}
              </Box>
            ) : (
              <CommonGrid
                columns={[
                  {
                    header: "Full Name",
                    accessor: "full_name",
                    sortable: true,
                  },
                  {
                    header: "Email",
                    accessor: "userEmail",
                    sortable: true,
                    render: (val, user) => (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {user.isVerified ? (
                          <FaCheckCircle color="green" title="Verified User" />
                        ) : (
                          <FaCheckCircle color="red" title="Unverified User" />
                        )}
                        <span style={{color:"black"}}>{val}</span>
                      </div>
                    ),
                  },
                  {
                    header: "Role",
                    accessor: "role_code",
                    sortable: true,
                    align: "center",
                  },
                  {
                    header: "Total Trees",
                    accessor: "tree_uploaded_count",
                    sortable: true,
                    align: "center",
                  },
                  {
                    header: "Tree Growth",
                    accessor: "tree_history_updated_count",
                    sortable: true,
                    align: "center",
                  },
                  {
                    header: "Points Collected",
                    accessor: "total_claimed_points",
                    sortable: true,
                    align: "center",
                  },
                ]}
                data={filteredUsers}
                loading={isLoading}
                variant="navy"
                pagination={true}
                pageSize={10}
                pageSizeOptions={[5, 10, 20, 50]}
                rowKey="id"
              />
            )}
          </Box>
        </div>
      </Box>
    </div>
  );
};

export default ProjectContributors;